import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Check,
  Calendar,
  ExternalLink,
  Eye,
  X,
  Receipt,
  RotateCcw,
  AlertCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
// Built-in showcase restaurants (matches templates_demo.py seed)
const SHOWCASE = [
  { id: 6163,  name: "The Coffee Commune",   slug: "the-coffee-commune-bowen-hills-6163" },
  { id: 14872, name: "Oxley Cafe",           slug: "oxley-cafe-oxley-14872" },
  { id: 14845, name: "Kebab Royale Oxley",   slug: "kebab-royale-oxley-oxley-14845" },
  { id: 12265, name: "Osteria Epoca",        slug: "osteria-epoca-yeronga-12265" },
];

const fmt = (n) => Number(n).toFixed(2);

const TemplateCard = ({ t, onApply, onPreview, isApplied }) => {
  const isFree = t.tier === "free";
  return (
    <div
      data-testid={`tpl-card-${t.id}`}
      className="rounded-2xl border border-stone-200 bg-white overflow-hidden flex flex-col hover:border-stone-400 transition-colors"
    >
      <div className="aspect-[16/9] bg-stone-200 overflow-hidden relative">
        <img
          src={t.preview_image}
          alt={t.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&q=70";
          }}
        />
        <div className="absolute top-2 left-2 flex gap-1.5">
          {!isFree && (
            <span className="px-2 py-0.5 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold uppercase tracking-wider">
              💎 Diamond Grade
            </span>
          )}
          {isFree && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider">
              Free
            </span>
          )}
          {t.currently_in_window && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider">
              In season
            </span>
          )}
        </div>
        {isApplied && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-stone-900 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
            <Check className="w-3 h-3" /> Active
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="font-serif text-lg text-stone-900 leading-tight">{t.name}</h3>
          <span className="font-bold text-stone-900 tabular-nums text-sm">
            {t.display_price_label}
          </span>
        </div>
        <p className="text-[12px] text-stone-500 mb-2">{t.tagline}</p>
        <p className="text-[13px] text-stone-700 flex-1 line-clamp-3">{t.description}</p>

        {t.active_window && (
          <div className="flex items-center gap-1 text-[11px] text-stone-500 mt-2">
            <Calendar className="w-3 h-3" />
            Auto-activates {t.active_window.start} → {t.active_window.end}
            {t.active_window.recurring_yearly ? " yearly" : ""}
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <button
            data-testid={`apply-${t.id}`}
            onClick={() => onApply(t)}
            className="flex-1 px-3 py-2 rounded-lg bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
          >
            {isFree ? "Apply for free" : "Activate · $" + t.price_aud_ex_gst}
          </button>
          <button
            data-testid={`preview-${t.id}`}
            onClick={() => onPreview(t)}
            className="px-3 py-2 rounded-lg border border-stone-200 text-stone-700 text-[13px] font-semibold flex items-center gap-1 hover:border-stone-400"
            title="Preview palette"
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
        </div>
      </div>
    </div>
  );
};

const PaletteStrip = ({ stylesheet }) => {
  const v = stylesheet?.css_vars || {};
  const colors = [
    { k: "--tpl-bg", l: "BG" },
    { k: "--tpl-surface", l: "Surface" },
    { k: "--tpl-ink", l: "Ink" },
    { k: "--tpl-accent", l: "Accent" },
    { k: "--tpl-line", l: "Line" },
  ];
  return (
    <div className="flex gap-1">
      {colors.map((c) => (
        <div key={c.k} className="flex flex-col items-center">
          <div
            className="w-8 h-8 rounded border border-stone-200"
            style={{ backgroundColor: v[c.k] || "#fff" }}
            title={`${c.l}: ${v[c.k]}`}
          />
          <div className="text-[9px] text-stone-400 mt-0.5">{c.l}</div>
        </div>
      ))}
    </div>
  );
};

const CheckoutModal = ({ template, restaurant, onClose, onSuccess }) => {
  const [payer, setPayer] = useState({
    name: restaurant?.name || "",
    email: "",
    abn: "",
  });
  const [stylesheet, setStylesheet] = useState(template.default_stylesheet);
  const [activateStart, setActivateStart] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [activateEnd, setActivateEnd] = useState(
    new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isFree = template.tier === "free";
  const pricing = template.pricing;

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      if (isFree) {
        // Free templates apply instantly — no Stripe
        const body = {
          template_id: template.id,
          restaurant_id: restaurant.id,
          stylesheet_id: stylesheet,
          ...payer,
          payer_abn: payer.abn,
        };
        const r = await fetch(`${API}/api/directory/templates/apply`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();
        onSuccess(j);
        return;
      }
      // Diamond — create Stripe Checkout session and redirect
      const body = {
        template_id: template.id,
        restaurant_id: restaurant.id,
        stylesheet_id: stylesheet,
        activate_start: activateStart,
        activate_end: activateEnd,
        payer_name: payer.name,
        payer_email: payer.email,
        payer_abn: payer.abn,
        origin_url: window.location.origin,
      };
      const r = await fetch(`${API}/api/payments/templates/checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      // Stash so the success page knows it just paid
      sessionStorage.setItem(
        "pending_template_purchase",
        JSON.stringify({ session_id: j.session_id, template_name: template.name }),
      );
      window.location.href = j.checkout_url;
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="checkout-modal"
    >
      <div
        className="bg-white w-full max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="font-serif text-xl text-stone-900">
            {isFree ? "Apply template" : "Activate Diamond Grade"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
          >
            <X className="w-4 h-4 text-stone-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-stone-50 rounded-xl px-3 py-2.5">
            <div className="text-[11px] uppercase tracking-wider text-stone-500">Template</div>
            <div className="font-serif text-lg text-stone-900">{template.name}</div>
            <div className="text-[12px] text-stone-600">{template.tagline}</div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-stone-700 mb-1.5">Apply to</div>
            <div className="px-3 py-2 rounded-lg bg-stone-50 border border-stone-200 text-[14px] text-stone-900 font-semibold">
              {restaurant.name}
            </div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-stone-700 mb-1.5">Stylesheet variant</div>
            <div className="grid grid-cols-2 gap-2">
              {template.stylesheets.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStylesheet(s.id)}
                  className={`px-3 py-2 rounded-lg border text-left ${
                    stylesheet === s.id
                      ? "border-stone-900 bg-stone-50"
                      : "border-stone-200 hover:border-stone-300"
                  }`}
                  data-testid={`stylesheet-${s.id}`}
                >
                  <div className="text-[12px] font-semibold text-stone-900 mb-1">{s.name}</div>
                  <PaletteStrip stylesheet={s} />
                </button>
              ))}
            </div>
          </div>

          {!isFree && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[12px] font-semibold text-stone-700 mb-1.5">Activate from</div>
                  <input
                    type="date"
                    value={activateStart}
                    onChange={(e) => setActivateStart(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[14px]"
                  />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-stone-700 mb-1.5">Until</div>
                  <input
                    type="date"
                    value={activateEnd}
                    onChange={(e) => setActivateEnd(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[14px]"
                  />
                </div>
              </div>

              <div>
                <div className="text-[12px] font-semibold text-stone-700 mb-1.5">Billed to</div>
                <input
                  placeholder="Business name"
                  value={payer.name}
                  onChange={(e) => setPayer({ ...payer, name: e.target.value })}
                  className="w-full px-3 py-2 mb-2 rounded-lg border border-stone-200 text-[14px]"
                  data-testid="payer-name"
                />
                <input
                  placeholder="Email for receipt"
                  type="email"
                  value={payer.email}
                  onChange={(e) => setPayer({ ...payer, email: e.target.value })}
                  className="w-full px-3 py-2 mb-2 rounded-lg border border-stone-200 text-[14px]"
                  data-testid="payer-email"
                />
                <input
                  placeholder="ABN (11 digits)"
                  value={payer.abn}
                  onChange={(e) => setPayer({ ...payer, abn: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[14px]"
                  data-testid="payer-abn"
                />
              </div>

              <div className="rounded-xl bg-stone-900 text-white p-4">
                <div className="flex justify-between text-[13px]">
                  <span>Subtotal (ex GST)</span>
                  <span className="font-bold tabular-nums">${fmt(pricing.subtotal_ex_gst)}</span>
                </div>
                <div className="flex justify-between text-[13px] mt-1">
                  <span>GST (10%)</span>
                  <span className="font-bold tabular-nums">${fmt(pricing.gst)}</span>
                </div>
                <div className="flex justify-between text-base mt-2 pt-2 border-t border-white/20">
                  <span className="font-semibold">Total inc GST</span>
                  <span className="font-bold tabular-nums">AUD ${fmt(pricing.total_inc_gst)}</span>
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[13px] text-rose-700">
              {error}
            </div>
          )}

          <button
            data-testid="confirm-apply"
            disabled={busy || (!isFree && (!payer.email || !payer.abn))}
            onClick={submit}
            className="w-full px-4 py-3 rounded-xl bg-stone-900 text-white font-semibold disabled:opacity-40"
          >
            {busy
              ? "Processing…"
              : isFree
              ? "Apply template"
              : `Pay $${fmt(pricing.total_inc_gst)} & activate`}
          </button>
          {!isFree && (
            <p className="text-[11px] text-stone-500 text-center">
              You'll receive a Tax Invoice with ABN by email. Demo mode skips Stripe.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const InvoiceModal = ({ invoice, onClose }) => (
  <div
    className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
    onClick={onClose}
    data-testid="invoice-modal"
  >
    <div
      className="bg-white w-full max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-5 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-emerald-600" />
          <h2 className="font-serif text-xl">Tax Invoice</h2>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center">
          <X className="w-4 h-4 text-stone-500" />
        </button>
      </div>
      <div className="p-5 space-y-3 text-[13px]">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-800">
          <div className="font-bold">Payment successful</div>
          <div className="text-[12px]">A copy has been emailed to {invoice.issued_to_email || "the billing email"}.</div>
        </div>
        <div className="space-y-1 text-stone-700">
          <div className="flex justify-between"><span className="text-stone-500">Tax Invoice #</span><span className="font-mono font-bold">{invoice.tax_invoice_number}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Issued</span><span>{new Date(invoice.issued_at).toLocaleString("en-AU")}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Supplier</span><span className="text-right">{invoice.supplier_name}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Supplier ABN</span><span className="font-mono">{invoice.supplier_abn}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Billed to</span><span className="text-right">{invoice.issued_to_name}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Customer ABN</span><span className="font-mono">{invoice.issued_to_abn || "—"}</span></div>
        </div>
        <div className="border-t border-stone-200 pt-3">
          <div className="flex justify-between text-stone-700">
            <span>{invoice.line_item.description}</span>
            <span className="tabular-nums">${fmt(invoice.line_item.unit_price_ex_gst)}</span>
          </div>
          <div className="flex justify-between text-stone-600 mt-1">
            <span>GST 10%</span>
            <span className="tabular-nums">${fmt(invoice.gst)}</span>
          </div>
          <div className="flex justify-between font-bold text-stone-900 mt-2 pt-2 border-t border-stone-200">
            <span>Total (inc GST)</span>
            <span className="tabular-nums">AUD ${fmt(invoice.total_inc_gst)}</span>
          </div>
        </div>
        <p className="text-[11px] text-stone-400 text-center">
          This is a tax invoice issued in accordance with A New Tax System (Goods and Services Tax) Act 1999.
        </p>
      </div>
    </div>
  </div>
);

export default function AdminTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("free");
  const [restaurantId, setRestaurantId] = useState(SHOWCASE[0].id);
  const [restaurantStates, setRestaurantStates] = useState({}); // id -> {active_template_id, active_diamond_id, ...}
  const [openTpl, setOpenTpl] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null); // {message, error}

  const restaurant = useMemo(
    () => SHOWCASE.find((r) => r.id === restaurantId),
    [restaurantId],
  );

  const loadTemplates = () => {
    fetch(`${API}/api/directory/templates`)
      .then((r) => r.json())
      .then((d) => setTemplates(d.templates || []));
  };

  const loadRestaurantStates = async () => {
    const out = {};
    await Promise.all(
      SHOWCASE.map(async (r) => {
        try {
          const j = await fetch(`${API}/api/directory/restaurants/${r.slug}`).then((x) => x.json());
          out[r.id] = {
            active_template: j.active_template,
            upcoming_diamond: j.upcoming_diamond,
          };
        } catch {
          out[r.id] = {};
        }
      }),
    );
    setRestaurantStates(out);
  };

  // Stripe Checkout return handler
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      setPollingStatus({ error: "Payment cancelled — no charge made." });
      window.history.replaceState({}, "", "/admin/templates");
      return;
    }
    if (!sessionId) return;

    setPollingStatus({ message: "Confirming payment…" });
    let attempts = 0;
    const maxAttempts = 12;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API}/api/payments/templates/status/${sessionId}`);
        const j = await r.json();
        if (j.payment_status === "paid") {
          clearInterval(interval);
          setPollingStatus(null);
          if (j.invoice) setInvoice(j.invoice);
          await loadRestaurantStates();
          sessionStorage.removeItem("pending_template_purchase");
          window.history.replaceState({}, "", "/admin/templates");
        } else if (
          j.status === "expired" ||
          j.payment_status === "unpaid" && attempts >= maxAttempts
        ) {
          clearInterval(interval);
          setPollingStatus({ error: "Payment not confirmed yet. Stripe will retry — check back in a minute." });
          window.history.replaceState({}, "", "/admin/templates");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setPollingStatus({ error: "Timed out waiting for Stripe. Refresh in a moment." });
          window.history.replaceState({}, "", "/admin/templates");
        }
      } catch (e) {
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setPollingStatus({ error: String(e) });
        }
      }
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTemplates();
    loadRestaurantStates();
  }, []);

  const state = restaurantStates[restaurantId] || {};
  const activeId =
    state.active_template?.id || state.upcoming_diamond?.id;

  const onPreview = (t) => {
    window.open(`/r/${restaurant.slug}`, "_blank");
  };

  const onApply = (t) => setOpenTpl(t);

  const onSuccess = async (resp) => {
    setOpenTpl(null);
    await loadRestaurantStates();
    if (resp.invoice) setInvoice(resp.invoice);
  };

  const revert = async (scope) => {
    await fetch(`${API}/api/directory/templates/revert/${restaurantId}?scope=${scope}`, { method: "POST" });
    await loadRestaurantStates();
  };

  const filtered = templates.filter((t) => t.tier === tab);

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/directory" className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <h1 className="font-serif text-xl text-stone-900 tracking-tight">Templates</h1>
          <span className="text-[12px] text-stone-400 hidden sm:inline">Admin · HustlePlate</span>
          <Link to="/admin/bookings" className="ml-auto text-[12px] text-stone-500 hover:text-stone-900 underline">
            View bookings →
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {pollingStatus && (
          <div
            data-testid="payment-status-banner"
            className={`mb-4 px-4 py-3 rounded-xl border ${
              pollingStatus.error
                ? "bg-rose-50 border-rose-200 text-rose-800"
                : "bg-amber-50 border-amber-200 text-amber-800"
            }`}
          >
            <div className="font-semibold text-[13px]">
              {pollingStatus.error || pollingStatus.message}
            </div>
            {pollingStatus.message && !pollingStatus.error && (
              <div className="text-[11px] mt-0.5">
                Stripe processes test payments in ~5 seconds. Don't refresh.
              </div>
            )}
          </div>
        )}

        {/* Restaurant selector */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4 mb-4">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">Applying to</div>
              <div className="font-serif text-2xl text-stone-900">{restaurant?.name}</div>
            </div>
            <Link
              to={`/r/${restaurant?.slug}`}
              target="_blank"
              className="text-[12px] text-stone-500 hover:text-stone-900 underline flex items-center gap-1"
              data-testid="view-storefront"
            >
              View storefront <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            {SHOWCASE.map((r) => (
              <button
                key={r.id}
                onClick={() => setRestaurantId(r.id)}
                data-testid={`pick-restaurant-${r.id}`}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${
                  r.id === restaurantId
                    ? "bg-stone-900 text-white border-stone-900"
                    : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                }`}
              >
                {r.name}
              </button>
            ))}
          </div>

          {/* Current state */}
          <div className="flex flex-wrap items-center gap-2 text-[12px]">
            {state.active_template ? (
              <>
                <span className="text-stone-500">Currently active:</span>
                <span className={`px-2 py-0.5 rounded-full font-semibold ${
                  state.active_template.tier === "diamond"
                    ? "bg-fuchsia-100 text-fuchsia-700"
                    : "bg-emerald-100 text-emerald-700"
                }`}>
                  {state.active_template.tier === "diamond" ? "💎 " : ""}
                  {state.active_template.name}
                </span>
                <button
                  onClick={() => revert(state.active_template.tier === "diamond" ? "diamond" : "all")}
                  className="ml-2 text-stone-500 hover:text-rose-600 flex items-center gap-1"
                  data-testid="revert-btn"
                >
                  <RotateCcw className="w-3 h-3" /> Revert
                </button>
              </>
            ) : (
              <span className="text-stone-500 italic">No template applied — using platform default</span>
            )}
            {state.upcoming_diamond && (
              <span className="ml-2 text-stone-400">
                <AlertCircle className="w-3 h-3 inline" /> {state.upcoming_diamond.name} configured but
                outside its active window ({state.upcoming_diamond.active_window?.start} → {state.upcoming_diamond.active_window?.end})
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            data-testid="tab-free"
            onClick={() => setTab("free")}
            className={`px-4 py-2 rounded-full text-[13px] font-semibold border ${
              tab === "free"
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white text-stone-700 border-stone-200"
            }`}
          >
            Free templates · {templates.filter((t) => t.tier === "free").length}
          </button>
          <button
            data-testid="tab-diamond"
            onClick={() => setTab("diamond")}
            className={`px-4 py-2 rounded-full text-[13px] font-semibold border ${
              tab === "diamond"
                ? "bg-fuchsia-600 text-white border-fuchsia-600"
                : "bg-white text-stone-700 border-stone-200"
            }`}
          >
            💎 Diamond Grade · {templates.filter((t) => t.tier === "diamond").length}
          </button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <TemplateCard
              key={t.id}
              t={t}
              isApplied={t.id === activeId}
              onApply={onApply}
              onPreview={onPreview}
            />
          ))}
        </div>

        <p className="text-[11px] text-stone-400 text-center mt-8">
          Prices to vendors exclude GST · GST added at checkout · Switching templates does not delete menu data
        </p>
      </div>

      {openTpl && restaurant && (
        <CheckoutModal
          template={openTpl}
          restaurant={restaurant}
          onClose={() => setOpenTpl(null)}
          onSuccess={onSuccess}
        />
      )}
      {invoice && <InvoiceModal invoice={invoice} onClose={() => setInvoice(null)} />}
    </div>
  );
}
